import { useSourcesService } from '@/services/sources';
import { HandleSubmitMessageType } from '@/utils/aiUtils';
import { isOverflown } from '@/utils/overflowUtils';
import {
  DtUIMessage,
  GetChatByIdResponse,
  SupportedRegion,
} from '@ditto/shared';
import { Source as BackendSource } from '@ditto/shared/dist/cjs/contract/sources';
import { debounce } from 'lodash';
import {
  Dispatch,
  SetStateAction,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type RefObject,
} from 'react';
import {
  useParsedMessages,
  type ParsedAssistantMessage,
} from './useMessageObject';
// Types
export interface DisplayedFile {
  fileId: string;
  pageNumber: number;
  type: 'sharedKnowledgeFile' | 'file';
  quotedTexts?: string[];
}

export interface Citation {
  fileId: string;
  pageNumber: number;
  quotedText: string;
  fileName: string;
}

// Source for knowledge base documents (has quotable text)
export interface FileSource {
  type: 'file';
  ids: string[];
  fileId: string;
  fileName: string;
  pageNumber: number;
  quotedText: string; // Required - actual text from document
  messageId: string;
  groupUname?: string;
}

// Source for FDA database tools (structured data, optional quote)
export interface DatabaseSource {
  type: 'database';
  ids: string[];
  databaseId: string;
  databaseName: string;
  databaseUrl: string;
  messageId: string;
  quotedText?: string; // Optional - only if there's actual quotable text
  toolName: string;
}

// Unified source type
export type Source = FileSource | DatabaseSource;

export interface ChatContextValue {
  messages: DtUIMessage[];
  sources: Source[]; // Unified sources (files + databases)
  isDoneStreaming: boolean;

  displayedFile: DisplayedFile | undefined;
  setDisplayedFile: Dispatch<SetStateAction<DisplayedFile | undefined>>;

  isScrollButtonVisible: boolean;
  scrollRef: RefObject<{ scrollToBottom: () => void } | null>;
  contentCbRef: (node: HTMLDivElement) => void;
  onContentUnmount: () => void;
  sendMessage: (message: HandleSubmitMessageType) => void;

  // Chat data
  chatData: GetChatByIdResponse | null;

  // Region management
  region: SupportedRegion;
  setRegion: (region: SupportedRegion) => void;
  regionInferenceRequired: boolean;
  setRegionInferenceRequired: (required: boolean) => void;
  parsedAssistantMessages: ParsedAssistantMessage[];

  // Feature flags
  showFollowUpQuestions: boolean;
}

// Context
const ChatContext = createContext<ChatContextValue>({
  messages: [],
  sources: [],
  isDoneStreaming: false,
  displayedFile: undefined,
  setDisplayedFile: () => {},
  isScrollButtonVisible: false,
  scrollRef: { current: { scrollToBottom: () => {} } },
  contentCbRef: () => {},
  onContentUnmount: () => {},
  sendMessage: () => {},
  chatData: null,
  region: 'WORLD',
  setRegion: () => {},
  regionInferenceRequired: false,
  setRegionInferenceRequired: () => {},
  parsedAssistantMessages: [],
  showFollowUpQuestions: true,
});

// Hook to access context
export const useChatCtx = () => {
  const context = useContext(ChatContext);
  if (!context) {
    throw new Error('useChatContext must be used within a ChatContextProvider');
  }
  return context;
};

// Utility functions
function checkIsScrollButtonVisible(node: HTMLDivElement): boolean {
  if (!node) return false;
  const isContainerOverflowing = isOverflown(node);
  if (!isContainerOverflowing) return false;

  // Use a threshold to account for floating point precision and sub-pixel scrolling
  const threshold = 10; // pixels
  const scrollBottom = node.scrollHeight - node.clientHeight;
  const isScrolledToBottom = node.scrollTop >= scrollBottom - threshold;

  return !isScrolledToBottom;
}

// Database configuration for different database types
const DATABASE_CONFIG = {
  fda510kSubmissionDbSearch: {
    name: 'FDA 510(k) Submission Database',
    url: 'https://www.accessdata.fda.gov/scripts/cdrh/cfdocs/cfpmn/pmn.cfm',
  },
  fdaDeviceClassificationDbSearch: {
    name: 'FDA Device Classification Database',
    url: 'https://www.accessdata.fda.gov/scripts/cdrh/cfdocs/cfpcd/classification.cfm',
  },
  fdaAdverseEventsSearch: {
    name: 'FDA Adverse Events Database',
    url: 'https://www.accessdata.fda.gov/scripts/cdrh/cfdocs/cfmaude/search.cfm',
  },
} as const;

function extractSourcesFromMessages(
  parsedAssistantMessages: ParsedAssistantMessage[],
  sourcesFiles: BackendSource[]
): Source[] {
  const parsedSources = parsedAssistantMessages
    .flatMap(message =>
      message.citations?.flatMap(citation => {
        if (citation?.toolName === 'knowledgeSearch') {
          const sourceFile = sourcesFiles.find(
            source => source.id === citation?.fileId
          );
          if (!sourceFile) {
            return null;
          }
          return {
            type: 'file',
            ids: [citation?.id],
            fileId: citation?.fileId,
            pageNumber: citation?.pageNumber,
            quotedText: citation?.quote,
            fileName: citation?.fileName,
            messageId: message.messageId,
            groupUname: sourceFile?.group?.uname,
          } as FileSource;
        }
        if (citation?.toolName) {
          return {
            type: 'database',
            ids: [citation?.id],
            databaseId: citation?.toolName,
            databaseName:
              DATABASE_CONFIG[
                citation?.toolName as keyof typeof DATABASE_CONFIG
              ]?.name,
            databaseUrl:
              DATABASE_CONFIG[
                citation?.toolName as keyof typeof DATABASE_CONFIG
              ]?.url,
            messageId: message.messageId,
            quotedText: citation?.quote,
            toolName: citation?.toolName,
          } as DatabaseSource;
        }
      })
    )
    .filter(Boolean) as Source[];

  // Combine all database sources by toolName AND messageId
  const dedupedSources = [
    ...parsedSources.filter(source => source.type === 'file'),
    ...Object.values(
      parsedSources
        .filter(
          (source): source is DatabaseSource => source.type === 'database'
        )
        .reduce<Record<string, DatabaseSource>>((acc, source) => {
          // Use messageId + toolName as the unique key
          const key = `${source.messageId}::${source.toolName}`;
          if (acc[key]) {
            // Merge ids for same (messageId, toolName)
            acc[key].ids = Array.from(
              new Set([...acc[key].ids, ...source.ids])
            );
          } else {
            // Make a shallow copy to avoid mutating parsedSources
            acc[key] = { ...source, ids: [...source.ids] };
          }
          return acc;
        }, {})
    ),
  ];
  return dedupedSources;
}

function checkIsDoneStreaming(messages: DtUIMessage[]): boolean {
  return messages
    .filter(message => message.role === 'assistant')
    .flatMap(message => message.parts)
    .filter(part => part.type === 'text')
    .every(part => part.state === 'done');
}

// Custom hook that manages all chat state
function useChatState(
  messages: DtUIMessage[] = [],
  sendMessage: (message: HandleSubmitMessageType) => void,
  chatData: GetChatByIdResponse | null = null,
  showFollowUpQuestions: boolean = true
): ChatContextValue {
  // State
  const [isScrollButtonVisible, setIsScrollButtonVisible] = useState(false);
  const [displayedFile, setDisplayedFile] = useState<DisplayedFile | undefined>(
    undefined
  );
  const [region, setRegion] = useState<SupportedRegion>(
    chatData?.region ?? 'WORLD'
  );
  const [regionInferenceRequired, setRegionInferenceRequired] =
    useState<boolean>(false);

  // Refs
  const mutationObserver = useRef<MutationObserver | null>(null);
  const scrollRef = useRef<{ scrollToBottom: () => void } | null>(null);

  // Scroll management
  const onContentUnmount = useCallback(() => {
    mutationObserver.current?.disconnect();
  }, []);

  const contentCbRef = useCallback((node: HTMLDivElement) => {
    if (!node) return;

    function handleScroll() {
      setIsScrollButtonVisible(checkIsScrollButtonVisible(node));
    }

    const debouncedHandleScroll = debounce(handleScroll, 50);

    // Add scroll listener
    node.addEventListener('scroll', debouncedHandleScroll);

    // Observe DOM mutations for content changes
    mutationObserver.current = new MutationObserver(debouncedHandleScroll);
    mutationObserver.current.observe(node, {
      childList: true,
      subtree: true,
    });

    // Set up scroll ref
    scrollRef.current = {
      scrollToBottom: () => {
        node.scrollTo({
          top: node.scrollHeight,
          behavior: 'smooth',
        });
      },
    };
  }, []);

  // Derived state
  const isDoneStreaming = useMemo(
    () => checkIsDoneStreaming(messages),
    [messages]
  );

  const { sources: sourcesFiles } = useSourcesService();

  // Parse messages into structured objects
  const parsedAssistantMessages = useParsedMessages(messages);

  const sources = useMemo(
    () => extractSourcesFromMessages(parsedAssistantMessages, sourcesFiles),
    [messages, parsedAssistantMessages, sourcesFiles]
  );

  // Handle region from message metadata
  useEffect(() => {
    const lastMessage = messages[messages.length - 1];
    if (lastMessage?.metadata) {
      // Check for regionInferenceRequired first - this takes priority
      if (lastMessage.metadata.regionInferenceRequired !== undefined) {
        setRegionInferenceRequired(
          lastMessage.metadata.regionInferenceRequired
        );
        // If regionInferenceRequired is true, don't set region from metadata
        // If it's false and region is present, we can set the region
        if (
          !lastMessage.metadata.regionInferenceRequired &&
          lastMessage.metadata.region
        ) {
          setRegion(lastMessage.metadata.region);
        }
      } else if (lastMessage.metadata.region) {
        // Only set region if regionInferenceRequired wasn't explicitly set
        // Update region from message metadata (messages take priority over chatData)
        setRegion(lastMessage.metadata.region);
        setRegionInferenceRequired(false);
      }
    }
  }, [messages]);

  // Update region when chatData changes (initial load only)
  // Only update if we haven't received region from messages
  useEffect(() => {
    // Default to WORLD if no region in chatData
    const regionFromChatData = chatData?.region ?? 'WORLD';
    // Only update from chatData if we haven't received regionInferenceRequired from messages
    const lastMessage = messages[messages.length - 1];
    const hasRegionInferenceFromMessage =
      lastMessage?.metadata?.regionInferenceRequired !== undefined;
    const hasRegionFromMessage = lastMessage?.metadata?.region !== undefined;

    // Only update from chatData if:
    // 1. No regionInferenceRequired is set from messages
    // 2. No region was set from message metadata (messages take priority)
    // 3. The current region doesn't match chatData (to avoid unnecessary updates)
    // 4. Region is WORLD (default) - don't override user selections
    if (
      !hasRegionInferenceFromMessage &&
      !hasRegionFromMessage &&
      region === 'WORLD' &&
      regionFromChatData !== 'WORLD'
    ) {
      setRegion(regionFromChatData);
      setRegionInferenceRequired(false);
    } else if (
      !hasRegionInferenceFromMessage &&
      !hasRegionFromMessage &&
      !region
    ) {
      // If region is not set at all, initialize from chatData
      setRegion(regionFromChatData);
      setRegionInferenceRequired(false);
    }
  }, [chatData?.region, messages, region]);

  return {
    messages,
    sources,
    isDoneStreaming,

    displayedFile,
    setDisplayedFile,

    isScrollButtonVisible,
    scrollRef,
    contentCbRef,
    onContentUnmount,
    sendMessage,

    chatData,
    region,
    setRegion,
    regionInferenceRequired,
    setRegionInferenceRequired,
    parsedAssistantMessages,
    showFollowUpQuestions,
  };
}

export interface ChatContextProviderProps {
  children: React.ReactNode;
  messages?: DtUIMessage[];
  sendMessage: (message: HandleSubmitMessageType) => void;
  chatData?: GetChatByIdResponse | null;
  showFollowUpQuestions?: boolean;
}

export const ChatContextProvider: React.FC<ChatContextProviderProps> = ({
  children,
  messages = [],
  sendMessage,
  chatData = null,
  showFollowUpQuestions = true,
}) => {
  const contextValue = useChatState(
    messages,
    sendMessage,
    chatData,
    showFollowUpQuestions
  );

  return (
    <ChatContext.Provider value={contextValue}>{children}</ChatContext.Provider>
  );
};
